<template>
  <div
    v-if="location?.last_refresh === null"
    class="fetching_data"
  >
    <p>We're currently fetching this locations data. Please come back later </p>
  </div>

  <div
    v-else-if="location?.account_connection?.status"
    class="error_state"
  >
    <p>
      There is an issue with your account connection. Please go to your
      <a href="/local/gmb/settings">settings</a>, remove this location and then add it again
    </p>
    <p>This page may not function correctly until this error is resolved.</p>
  </div>

  <div v-else>
    <listing-header />

    <div
      v-if="location"
      :class="[
        'listing_content',
        updated ? 'expanded' : '',
      ]"
    >
      <div class="left_side">
        <div class="tabs">
          <tab-button
            :active="selectedTab === 1"
            icon="icons/edit_pencil"
            @select="selectedTab = 1"
          >
            Edit My Listing
          </tab-button>
          <tab-button
            :active="selectedTab === 3"
            icon="icons/media"
            @select="selectedTab = 3"
          >
            Manage Media
          </tab-button>
        </div>

        <edit-listing v-if="selectedTab === 1" />
        <manage-media v-if="selectedTab === 3" />
      </div>

      <div class="right_side">
        <listing-view />
      </div>

      <div
        v-if="updated"
        class="sticky_update_bar"
      >
        <primary-button
          :class="[
            'update_btn',
            locationLoading ? 'disabled' : '',
          ]"
          size="md"
          @click="saveLocationChanges(location)"
        >
          {{ locationLoading ? 'Saving' : 'Save' }} changes
          <i
            v-if="locationLoading"
            class="fa fa-spinner fa-spin"
          />
        </primary-button>
      </div>
    </div>

    <div
      v-if="!location"
      class="loading_location"
    >
      Loading
      <i class="fa fas fa-spin fa-spinner" />
    </div>

    <media-item-preview v-if="mediaItemPreviewModalOpen" />
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import ListingHeader from './includes/ListingHeader';
import EditListing from "./includes/EditListing";
import ListingView from "./includes/ListingView";
import ManageMedia from "./includes/ManageMedia";
import PrimaryButton from "../../global/buttons/PrimaryButton";
import TabButton from "../../global/buttons/TabButton";
import MediaItemPreview from './modals/MediaItemPreview'

export default {
  components: {
    TabButton,
    PrimaryButton,
    ManageMedia,
    ListingView,
    EditListing,
    ListingHeader,
    MediaItemPreview,
  },
  props: {
    locationId: {
      type: Number,
      default: null,
    }
  },
  data() {
    return {
      selectedTab: 1,
    }
  },
  computed: {
    ...mapGetters('businessProfile', [
      'selectedLocation',
      'location',
      'updated',
      'mediaItemPreviewModalOpen',
      'locationLoading'
    ])
  },
  watch: {
    selectedLocation: {
      handler() {
        this.clearLocation();
        this.clearAllMedia();
        this.fetchLocation(this.selectedLocation)
            .then(() => {
              this.fetchGbpCategories();
            })
        this.fetchAllMediaItems(this.selectedLocation);
      }
    }
  },
  beforeMount() {
    this.getUser();
    this.updateSelectedLocation(this.locationId)
        .then(() => {
          window.onbeforeunload = () => {
            if (this.updated) {
              return true;
            }
          }
        });
  },
  methods: {
    ...mapActions('user', [
        'getUser'
    ]),
    ...mapActions('businessProfile', [
      'updateSelectedLocation',
      'fetchConnectedLocations',
      'clearLocation',
      'clearAllMedia',
      'fetchLocation',
      'fetchAllMediaItems',
      'saveLocationChanges',
      'fetchGbpCategories',
    ]),
  },
}
</script>

<style lang="scss" scoped>
.listing_content {
  display: flex;
  flex-wrap: wrap-reverse;
  justify-content: space-between;

  &.expanded {
    margin-bottom: 61px;
  }

  > .left_side {
    width: calc(60% - 1rem);

    > .tabs {
      width: 100%;
      display: flex;
      align-items: center;
      margin-bottom: 1rem;
    }
  }

  > .right_side {
    width: calc(40% - 1rem);
  }

  > .sticky_update_bar {
    position: fixed;
    bottom: 0;
    width: 100%;
    border-top: 1px solid #E8E5E2;
    background-color: #FFFFFF;
    margin-left: -30px;
    padding: .5rem 1rem;

    > .update_btn {
      margin-left: auto;
    }
  }
}

.loading_location {
  width: 100%;
  height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;

  i {
    margin-left: 1rem;
  }
}

.error_state {
  padding: 1rem 0.5rem;
  border: 1px solid var(--negative-color);
  border-radius: 0.75rem;
  text-align: center;
  margin: 1.5rem 0;

  :deep(p) {
    color: var(--negative-color);
    margin-bottom: .25rem;

    &:last-of-type {
      margin-bottom: 0
    }

    a {
      color: rgb(26, 13, 171);
    }
  }
}
</style>
